import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Alert,
  CardBody,
  Button,
  Label,
  Input,
  FormFeedback,
  Form,
  Spinner,
} from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useSelector, useDispatch } from "react-redux";
import withRouter from "components/Common/withRouter";

import Breadcrumb from "../../components/Common/Breadcrumb";
import avatar from "../../assets/images/users/user.png";

import { updateUser, getUser, clearErrors } from "../../store/actions";

const UserProfile = props => {
  document.title = "Profile | Skote - React Admin & Dashboard Template";

  const dispatch = useDispatch();

  const { user: loggedUser } = useSelector(state => state.Login);
  const userId = loggedUser?.id;

  const { selectedUser, getUserLoading, updateUserLoading, message } = useSelector(
    (state) => state.Dashboard
  );
  const userFromApi = selectedUser?.user;

  useEffect(() => {
    if (userId) dispatch(getUser(userId));
  }, [dispatch, userId]);

  useEffect(() => {
    if (message) {
      const t = setTimeout(() => dispatch(clearErrors()), 8000);
      return () => clearTimeout(t);
    }
  }, [message, dispatch]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      firstName: (userFromApi?.firstName ?? loggedUser?.firstName ?? "").trim(),
      lastName: (userFromApi?.lastName ?? loggedUser?.lastName ?? "").trim(),
      email: (userFromApi?.email ?? loggedUser?.email ?? "").trim(),
      password: "",
    },
    validationSchema: Yup.object({
      firstName: Yup.string().trim().required("Please enter your first name"),
      lastName: Yup.string().trim().required("Please enter your last name"),
      password: Yup.string().trim().min(8, "Password must be at least 8 characters").optional(),
    }),
    onSubmit: (values) => {
      const payload = {
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        ...(values.password ? { password: values.password } : {}),
      };

      dispatch(updateUser(payload, undefined, undefined, getUser, userId));
      validation.setFieldValue("password", "");
    },
  });

  const fullName = `${validation.values.firstName} ${validation.values.lastName}`.trim();

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title="Skote" breadcrumbItem="Profile" />

        {!!message && (
          <Alert color="danger" className="mb-3">
            {String(message)}
          </Alert>
        )}

        {getUserLoading ? (
          <div
            className="loading-container"
            style={{ width: "100%", height: "50dvh", display: "grid", placeItems: "center" }}
          >
            <Spinner />
          </div>
        ) : (
          <>
            <Row>
              <Col lg="12">
                <Card>
                  <CardBody>
                    <div className="d-flex">
                      <div>
                        <img
                          src={avatar}
                          alt="avatar"
                          className="avatar-md rounded-circle img-thumbnail"
                        />
                      </div>
                      <div className="flex-grow-1 align-self-center ms-3">
                        <div className="text-muted">
                          <h5 className="mb-1">{fullName || "—"}</h5>
                          <p className="mb-1">{validation.values.email || "—"}</p>
                          <p className="mb-0">Id no: #{userId || "—"}</p>
                        </div>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            <h4 className="card-title mb-4">Update Profile</h4>

            <Card>
              <CardBody>
                <Form
                  className="form-horizontal"
                  onSubmit={(e) => {
                    e.preventDefault();
                    validation.handleSubmit();
                  }}
                >
                  <Row>
                    <Col md="6">
                      <div className="form-group mb-3">
                        <Label className="form-label">First name</Label>
                        <Input
                          name="firstName"
                          type="text"
                          placeholder="Super"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.firstName}
                          invalid={validation.touched.firstName && !!validation.errors.firstName}
                        />
                        {validation.touched.firstName && validation.errors.firstName ? (
                          <FormFeedback type="invalid">
                            {validation.errors.firstName}
                          </FormFeedback>
                        ) : null}
                      </div>
                    </Col>
                    <Col md="6">
                      <div className="form-group mb-3">
                        <Label className="form-label">Last name</Label>
                        <Input
                          name="lastName"
                          type="text"
                          placeholder="Admin"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.lastName}
                          invalid={validation.touched.lastName && !!validation.errors.lastName}
                        />
                        {validation.touched.lastName && validation.errors.lastName ? (
                          <FormFeedback type="invalid">
                            {validation.errors.lastName}
                          </FormFeedback>
                        ) : null}
                      </div>
                    </Col>
                  </Row>

                  <Row>
                    <Col md="6">
                      <div className="form-group mb-3">
                        <Label className="form-label">Email</Label>
                        <Input name="email" type="email" value={validation.values.email} disabled />
                      </div>
                    </Col>
                    <Col md="6">
                      <div className="form-group mb-3">
                        <Label className="form-label">New password</Label>
                        <Input
                          name="password"
                          type="password"
                          placeholder="********"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.password}
                          invalid={validation.touched.password && !!validation.errors.password}
                          autoComplete="new-password"
                        />
                        {validation.touched.password && validation.errors.password ? (
                          <FormFeedback type="invalid">
                            {validation.errors.password}
                          </FormFeedback>
                        ) : null}
                      </div>
                    </Col>
                  </Row>

                  <div className="action-btn-container">
                    {/* <Button
                      color="secondary"
                      disabled={updateUserLoading}
                      onClick={() => props.router.navigate(-1)}
                    >
                      Cancel
                    </Button> */}
                    <Button
                      color="primary"
                      type="submit"
                      disabled={updateUserLoading}
                    >
                      {updateUserLoading ? "Updating..." : "Update Profile"}
                    </Button>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </>
        )}
      </Container>
    </div>
  );
};

export default withRouter(UserProfile);
